import React, { type FC, useCallback, useState } from 'react'

import { ApiError } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Error, extendDefaultMatchers } from '@/components/ui/Error.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import {
  type CreateFolderFormValues,
  EditCategorySchema,
} from '@/dialogs/EditScriptsFolderDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { editScriptsCategoryFx } from '@/models/scripts/categories.ts'

interface Props extends DialogProps {
  rowId: number
  prevName: string
}

const editErrorMatchers = extendDefaultMatchers({
  'This folder name already exists': { status: 409 },
})

export const EditScriptsFolderDialog: FC<Props> = ({
  rowId,
  prevName,
  isOpen,
  onClose,
}) => {
  const inviteFormValues = {
    folderName: prevName || '',
  }
  const [error, setError] = useState<Nullable<ApiError>>(null)
  const isLoading = useUnit(editScriptsCategoryFx.pending)

  const editForm = useForm<CreateFolderFormValues>({
    defaultValues: inviteFormValues,
    resolver: zodResolver(EditCategorySchema),
  })

  const {
    handleSubmit,
    formState: { errors },
  } = editForm

  const onSubmit = useCallback(
    async (values: CreateFolderFormValues) => {
      const name = values.folderName

      try {
        await editScriptsCategoryFx({ name, id: rowId })

        onClose()
      } catch (error) {
        setError(error as ApiError)
      }
    },
    [onClose, rowId],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header className="items-center">
        <Dialog.Title className="text-3xl">Edit Script Folder</Dialog.Title>
      </Dialog.Header>
      <FormProvider {...editForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={editForm.control}
            name="folderName"
            render={({ field }) => (
              <div className="mt-6 flex flex-col gap-2">
                <label className="text-sm font-medium text-title">
                  Folder Name
                </label>
                <TextField
                  {...field}
                  testId="edit-folder-name-input"
                  placeholder="Enter Name"
                />
                {errors.folderName && (
                  <span className="text-danger-default">
                    {errors.folderName.message}
                  </span>
                )}
              </div>
            )}
          />
          <Error
            error={error}
            matchers={editErrorMatchers}
            closable
            className="mt-4"
          />
          <Dialog.ClosableFooter onClose={onClose}>
            <Button
              type="submit"
              className="items-center"
              isLoading={isLoading}
              testId="edit-folder-button"
            >
              Edit
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
